/**
 * Techbros B2B / Wholesale RFQ — client-side inquiry list.
 *
 * The wholesale ecosystem deliberately never reaches checkout. Instead of a
 * cart, verified trade partners build an "inquiry list" of SKUs they want a
 * quote on. The list is persisted in localStorage and serialized into the
 * wholesale lead-capture form on submit, so the merchant receives a single
 * structured quotation request (no order, no payment).
 *
 * Components:
 *   <b2b-inquiry-button>  — "Add to Inquiry List" control on PDP / quick grid.
 *   <b2b-inquiry-form>    — wraps the {% form 'contact' %} on the wholesale page;
 *                           injects the serialized list before submit + renders
 *                           a live, editable summary of selected items.
 *   <b2b-inquiry-count>   — small badge that reflects the current item count.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'techbros_rfq_inquiry_v1';

  /* Localized strings (window.theme.strings.b2b, populated in theme.liquid).
     English fallbacks keep the component working if the keys are ever absent. */
  var STR = (window.theme && window.theme.strings && window.theme.strings.b2b) || {};
  function t(key, fallback) {
    return STR[key] != null && STR[key] !== '' ? STR[key] : fallback;
  }

  function readList() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      var parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  function writeList(list) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } catch (e) {
      /* storage unavailable (private mode) — degrade silently */
    }
    document.dispatchEvent(
      new CustomEvent('on:b2b-inquiry:change', { detail: { list: list } })
    );
  }

  function escapeHtml(str) {
    return String(str == null ? '' : str).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* Clamp a requested qty to the variant's MOQ / increment rule. */
  function clampQty(qty, min, step) {
    qty = parseInt(qty, 10) || 1;
    min = parseInt(min, 10) || 1;
    step = parseInt(step, 10) || 1;
    if (qty < min) qty = min;
    var over = qty - min;
    if (step > 1 && over % step !== 0) qty = min + Math.ceil(over / step) * step;
    return qty;
  }

  function addItem(item) {
    if (!item || !item.id) return;
    var list = readList();
    var existing = list.filter(function (i) {
      return String(i.id) === String(item.id);
    })[0];
    if (existing) {
      existing.min = parseInt(item.min, 10) || existing.min || 1;
      existing.step = parseInt(item.step, 10) || existing.step || 1;
      existing.qty = clampQty(
        (parseInt(existing.qty, 10) || 0) + (parseInt(item.qty, 10) || 1),
        existing.min,
        existing.step
      );
    } else {
      list.push({
        id: String(item.id),
        sku: item.sku || '',
        title: item.title || '',
        qty: clampQty(item.qty, item.min, item.step),
        min: parseInt(item.min, 10) || 1,
        step: parseInt(item.step, 10) || 1
      });
    }
    writeList(list);
  }

  function removeItem(id) {
    writeList(
      readList().filter(function (i) {
        return String(i.id) !== String(id);
      })
    );
  }

  function serialize(list) {
    if (!list.length) return '';
    return list
      .map(function (i) {
        return (
          '• ' +
          (i.title || t('item', 'Item')) +
          (i.sku ? ' [' + t('sku', 'SKU') + ' ' + i.sku + ']' : '') +
          ' — ' + t('qty', 'qty') + ' ' +
          i.qty
        );
      })
      .join('\n');
  }

  /* ----------------------------------------------------------------------- */
  /* Anti-spam form guard: honeypot + minimum dwell time + double-submit lock.
     Bots that auto-fill every field trip the honeypot; bots that POST the form
     instantly trip the dwell-time check. Humans never notice either. */

  var PAGE_LOADED_AT = Date.now();
  var MIN_DWELL_MS = 4000;
  var FREE_MAIL = /@(gmail|googlemail|yahoo|ymail|hotmail|outlook|live|msn|icloud|me|aol|proton|protonmail|mail|gmx|zoho|yandex)\./i;

  function formGuardPasses(form, event) {
    var hp = form.querySelector('[data-ws-hp] input');
    if (hp && hp.value) {
      event.preventDefault();
      return false; /* silent drop — don't teach the bot */
    }
    if (Date.now() - PAGE_LOADED_AT < MIN_DWELL_MS) {
      event.preventDefault();
      return false;
    }
    var btn = form.querySelector('[type="submit"]');
    if (btn) {
      if (btn.dataset.wsSubmitting) {
        event.preventDefault();
        return false;
      }
      btn.dataset.wsSubmitting = '1';
      btn.setAttribute('aria-disabled', 'true');
      window.setTimeout(function () {
        delete btn.dataset.wsSubmitting;
        btn.removeAttribute('aria-disabled');
      }, 8000);
    }
    return true;
  }

  /* Guest wholesale application: guard + soft business-email nudge. */
  function initApplicationForm() {
    var form = document.getElementById('wholesale-apply-contact');
    if (!form) return;
    form.addEventListener('submit', function (event) {
      formGuardPasses(form, event);
    });
    var email = form.querySelector('[name="contact[email]"]');
    var warning = form.querySelector('[data-ws-email-warning]');
    if (email && warning) {
      email.addEventListener('blur', function () {
        warning.hidden = !FREE_MAIL.test(email.value);
      });
    }
  }

  /* Authenticated inquiry grid: instant client-side SKU/title filter. */
  function initGridFilter() {
    var input = document.querySelector('[data-b2b-grid-filter]');
    var grid = document.querySelector('.b2b-order-grid tbody');
    if (!input || !grid) return;
    var rows = Array.prototype.slice.call(grid.querySelectorAll('tr'));
    input.addEventListener('input', function () {
      var q = input.value.trim().toLowerCase();
      rows.forEach(function (row) {
        row.hidden = q !== '' && row.textContent.toLowerCase().indexOf(q) === -1;
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initApplicationForm();
      initGridFilter();
    });
  } else {
    initApplicationForm();
    initGridFilter();
  }

  /* ----------------------------------------------------------------------- */

  class B2BInquiryButton extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-b2b-add]');
      if (!this.button) return;
      this._onClick = this.handleClick.bind(this);
      this.button.addEventListener('click', this._onClick);
    }

    disconnectedCallback() {
      if (this.button && this._onClick) {
        this.button.removeEventListener('click', this._onClick);
      }
    }

    handleClick(event) {
      event.preventDefault();
      /* The qty input sits NEXT TO this element (inside .b2b-inquiry__row /
         [data-b2b-inquiry]), not inside it — search the shared wrapper. */
      var scope =
        this.closest('[data-b2b-inquiry]') ||
        this.closest('.b2b-inquiry__row') ||
        this.closest('td') ||
        this.closest('form') ||
        this;
      var qtyInput = scope.querySelector('[name="quantity"]');
      addItem({
        id: this.dataset.variantId || this.dataset.productId,
        sku: this.dataset.sku,
        title: this.dataset.title,
        qty: qtyInput ? qtyInput.value : this.dataset.min || 1,
        min: this.dataset.min,
        step: (qtyInput && qtyInput.step) || 1
      });
      if (qtyInput) qtyInput.value = clampQty(qtyInput.value, this.dataset.min, qtyInput.step);
      this.confirm();
    }

    confirm() {
      var label = this.button.querySelector('[data-b2b-add-label]') || this.button;
      var original = label.textContent;
      label.textContent = t('added', 'Added to inquiry ✓');
      this.button.classList.add('is-added');
      var self = this;
      window.clearTimeout(this._t);
      this._t = window.setTimeout(function () {
        label.textContent = original;
        self.button.classList.remove('is-added');
      }, 2000);
    }
  }

  class B2BInquiryCount extends HTMLElement {
    connectedCallback() {
      this.render = this.render.bind(this);
      document.addEventListener('on:b2b-inquiry:change', this.render);
      this.render();
    }
    disconnectedCallback() {
      document.removeEventListener('on:b2b-inquiry:change', this.render);
    }
    render() {
      var count = readList().length;
      this.textContent = count > 0 ? String(count) : '';
      this.hidden = count === 0;
      /* If wrapped in a bubble (e.g. the header cart-count badge), toggle it too. */
      var bubble = this.closest('.header__inquiry-count');
      if (bubble) bubble.hidden = count === 0;
    }
  }

  class B2BInquiryForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.field = this.querySelector('[data-b2b-inquiry-field]');
      this.summary = this.querySelector('[data-b2b-inquiry-summary]');
      this.render = this.render.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      document.addEventListener('on:b2b-inquiry:change', this.render);
      if (this.summary) {
        this.summary.addEventListener('click', this.onSummaryClick.bind(this));
        this.summary.addEventListener('change', this.onSummaryQty.bind(this));
      }
      if (this.form) this.form.addEventListener('submit', this.onSubmit);
      /* Successful post (marker rendered by Liquid) — clear the inquiry list. */
      if (this.querySelector('[data-b2b-clear]')) writeList([]);
      this.render();
    }

    disconnectedCallback() {
      document.removeEventListener('on:b2b-inquiry:change', this.render);
    }

    onSummaryClick(event) {
      var trigger = event.target.closest('[data-b2b-remove]');
      if (!trigger) return;
      event.preventDefault();
      removeItem(trigger.dataset.b2bRemove);
    }

    onSummaryQty(event) {
      var input = event.target.closest('[data-b2b-qty]');
      if (!input) return;
      var list = readList();
      var item = list.filter(function (i) {
        return String(i.id) === String(input.dataset.b2bQty);
      })[0];
      if (!item) return;
      item.qty = clampQty(input.value, item.min, item.step);
      writeList(list);
    }

    onSubmit(event) {
      var list = readList();
      if (this.field) this.field.value = serialize(list);
      /* Require either selected items or a written requirement. */
      var notes = this.form.querySelector('[name="contact[body]"]');
      if (!list.length && (!notes || !notes.value.trim())) {
        event.preventDefault();
        this.showError(t('errorEmpty', 'Add at least one product to your inquiry, or describe your requirements in the notes field.'));
        return;
      }
      if (!formGuardPasses(this.form, event)) return;
      this.clearError();
    }

    showError(msg) {
      this.clearError();
      var p = document.createElement('p');
      p.className = 'wholesale__form-error alert';
      p.setAttribute('role', 'alert');
      p.dataset.b2bError = '';
      p.textContent = msg;
      this.form.insertBefore(p, this.form.firstChild);
      p.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    clearError() {
      var existing = this.form.querySelector('[data-b2b-error]');
      if (existing) existing.remove();
    }

    render() {
      var list = readList();
      if (this.field) this.field.value = serialize(list);
      if (!this.summary) return;
      if (!list.length) {
        this.summary.innerHTML =
          '<p class="b2b-inquiry-summary__empty">' +
          escapeHtml(t('empty', 'No products added yet. Browse the catalogue and use “Add to Inquiry List”, or describe your requirements below.')) +
          '</p>';
        return;
      }
      var rows = list
        .map(function (i) {
          var min = parseInt(i.min, 10) || 1;
          var step = parseInt(i.step, 10) || 1;
          var title = i.title || t('item', 'Item');
          var qtyLabel = t('quantityFor', 'Quantity for [title]').replace('[title]', title);
          return (
            '<li class="b2b-inquiry-summary__row">' +
            '<span class="b2b-inquiry-summary__title">' +
            escapeHtml(title) +
            (i.sku ? ' <small>' + escapeHtml(t('sku', 'SKU')) + ' ' + escapeHtml(i.sku) + '</small>' : '') +
            (min > 1 ? ' <small>(' + escapeHtml(t('min', 'min')) + ' ' + min + (step > 1 ? ' · x' + step : '') + ')</small>' : '') +
            '</span>' +
            '<input type="number" class="input b2b-inquiry-summary__qty-input" value="' + (parseInt(i.qty, 10) || min) +
            '" min="' + min + '" step="' + step + '" inputmode="numeric" data-b2b-qty="' + escapeHtml(i.id) +
            '" aria-label="' + escapeHtml(qtyLabel) + '">' +
            '<span class="b2b-inquiry-summary__qty">' + escapeHtml(t('units', 'units')) + '</span>' +
            '<button type="button" class="b2b-inquiry-summary__remove" data-b2b-remove="' +
            escapeHtml(i.id) +
            '" aria-label="' + escapeHtml(t('remove', 'Remove')) + '">×</button>' +
            '</li>'
          );
        })
        .join('');
      this.summary.innerHTML = '<ul class="b2b-inquiry-summary__list">' + rows + '</ul>';
    }
  }

  if (!customElements.get('b2b-inquiry-button')) {
    customElements.define('b2b-inquiry-button', B2BInquiryButton);
  }
  if (!customElements.get('b2b-inquiry-count')) {
    customElements.define('b2b-inquiry-count', B2BInquiryCount);
  }
  if (!customElements.get('b2b-inquiry-form')) {
    customElements.define('b2b-inquiry-form', B2BInquiryForm);
  }
})();
